/**
 * Shared helpers for the chat search panels (in-channel and all-chats), so the
 * two surfaces render timestamps and highlights identically, absorb typed
 * operators the same way, and describe the same controls.
 */

import {
  isChatSearchDay,
  parseChatSearchQuery,
  type ChatSearchHasFilter,
  type ChatSearchMatchMode,
} from '@fias/platform-resources';

/** Compact absolute timestamp — search results span history, so relative ages read poorly. */
export function formatSearchTimestamp(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Splits `text` on each case-insensitive occurrence of any search term so the
 * matched words can be marked. Terms come from the user's own query, and the
 * pieces are rendered as React children (never as HTML), so there is no
 * injection path here.
 */
export function highlightSegments(
  text: string,
  terms: string[],
): Array<{ text: string; hit: boolean }> {
  const escaped = terms
    .filter((t) => t.length > 0)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return [{ text, hit: false }];
  const source = `(${escaped.join('|')})`;
  // `split` with a single capture group interleaves the captured matches into
  // the result, so odd indices are exactly the hits. Testing each piece with a
  // /g/ regex instead would be wrong — lastIndex carries between calls.
  return text
    .split(new RegExp(source, 'gi'))
    .map((piece, i) => ({ text: piece, hit: i % 2 === 1 }))
    .filter((seg) => seg.text.length > 0);
}

/**
 * Terms to mark in a result snippet for the given match mode. `contains`
 * matches the literal text, so the whole query is one term; the word modes
 * match each word independently.
 */
export function searchTermsForHighlight(
  trimmedQuery: string,
  hasTerms: boolean,
  matchMode: ChatSearchMatchMode = 'word',
): string[] {
  if (!hasTerms) return [];
  if (matchMode === 'contains') return [trimmedQuery];
  return trimmedQuery.split(/\s+/).filter(Boolean);
}

/** Empty-state hint per match mode, pointing at the broader mode. */
export const NO_MATCH_HINT: Record<ChatSearchMatchMode, string> = {
  word: 'Whole-word search. Try “Contains” to match part of a word.',
  contains: 'No message contains that text.',
};

// ---------------------------------------------------------------------------
// Filter controls — the values the panels' Has and Date controls trade in.
// ---------------------------------------------------------------------------

/** The content filter control's value; `null` is "Any" — no content filter. */
export type SearchHasValue = ChatSearchHasFilter | null;

/** A local calendar day as `YYYY-MM-DD`, or null for "no bound". */
export interface SearchDayRange {
  after: string | null;
  before: string | null;
}

export const EMPTY_SEARCH_DAY_RANGE: SearchDayRange = { after: null, before: null };

/**
 * True when both bounds are set and "after" is not earlier than "before" — a
 * range that can match nothing and that the server would refuse. One
 * definition, used by the control (to show why) and the controls hook (to hold
 * the search).
 */
export function isDayRangeInvalid(range: SearchDayRange): boolean {
  return !!range.after && !!range.before && range.after >= range.before;
}

// ---------------------------------------------------------------------------
// Typed operators
// ---------------------------------------------------------------------------

/** The operators a search box may absorb into its filter controls. */
export type AbsorbableOperator = 'from' | 'in' | 'after' | 'before' | 'has';

/** Every operator, in the order the hint shows them. */
const OPERATOR_EXAMPLES: ReadonlyArray<{ op: AbsorbableOperator; example: string }> = [
  { op: 'from', example: 'from:name' },
  { op: 'in', example: 'in:#channel' },
  { op: 'has', example: 'has:link' },
  { op: 'after', example: 'after:2030-01-01' },
  { op: 'before', example: 'before:2030-02-01' },
];

/**
 * The one-line syntax hint for a surface, built from the operators that
 * surface actually absorbs — so the hint can never advertise an operator the
 * box would leave as plain text.
 */
export function operatorHint(allowed: ReadonlySet<AbsorbableOperator>): string {
  const examples = OPERATOR_EXAMPLES.filter((e) => allowed.has(e.op)).map((e) => e.example);
  return `Tip: ${examples.join('  ')}`;
}

export interface AbsorbedOperators {
  /** The box's text with the absorbed operators removed; `raw` when nothing was. */
  text: string;
  from?: string;
  in?: string;
  after?: string;
  before?: string;
  has?: ChatSearchHasFilter;
}

/** Each whitespace-delimited token of `settled`, with where it sits in the string. */
function tokenSpans(settled: string): Array<{ token: string; start: number; end: number }> {
  const spans: Array<{ token: string; start: number; end: number }> = [];
  const pattern = /\S*?:"[^"]*"|\S+/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(settled)) !== null) {
    spans.push({ token: match[0], start: match.index, end: match.index + match[0].length });
  }
  return spans;
}

/**
 * Pulls COMPLETED, ALLOWED operators out of the search box as the user types,
 * so `from:bob ` becomes the From control's value and leaves the box — the
 * Slack convention, where a finished operator turns into a filter chip.
 *
 * What makes this safe to run on every keystroke:
 * - Only the settled part of the text (everything before the last whitespace)
 *   is considered; the token still being typed is never touched, so `from:bo`
 *   cannot become a filter for "bo" mid-word.
 * - An unterminated quote in the settled part means a quoted value is still
 *   being typed (`in:"Design ` on the way to `in:"Design Team"`), so nothing
 *   is absorbed yet.
 * - When nothing is absorbed, `text` is `raw` byte for byte — no
 *   re-serialisation, no whitespace changes — so the caret never jumps. An
 *   operator this surface does not accept (`in:` inside one channel) simply
 *   stays where the user typed it, as words.
 * - When something IS absorbed, only those tokens (and the whitespace after
 *   each) are cut out; every other character keeps its place.
 */
export function absorbChatSearchOperators(
  raw: string,
  allowed: ReadonlySet<AbsorbableOperator>,
): AbsorbedOperators {
  const lastBreak = raw.search(/\s\S*$/);
  if (lastBreak < 0) return { text: raw };
  const settled = raw.slice(0, lastBreak + 1);
  if ((settled.match(/"/g) ?? []).length % 2 === 1) return { text: raw };

  const absorbed: AbsorbedOperators = { text: raw };
  const cuts: Array<{ start: number; end: number }> = [];

  for (const { token, start, end } of tokenSpans(settled)) {
    const colon = token.indexOf(':');
    if (colon <= 0) continue;
    const name = token.slice(0, colon).toLowerCase() as AbsorbableOperator;
    if (!allowed.has(name)) continue;
    // Let the shared grammar decide whether this token is a well-formed
    // operator (valid day, known has: value, non-empty); it is the one parser.
    const parsed = parseChatSearchQuery(token);
    const value = parsed[name];
    if (value === undefined) continue;
    if (name === 'has') absorbed.has = value as ChatSearchHasFilter;
    else absorbed[name] = value as string;
    // Cut the token plus the whitespace that followed it.
    const trailing = /^\s*/.exec(settled.slice(end))?.[0].length ?? 0;
    cuts.push({ start, end: end + trailing });
  }

  if (cuts.length === 0) return { text: raw };
  let text = '';
  let cursor = 0;
  for (const cut of cuts) {
    text += raw.slice(cursor, cut.start);
    cursor = cut.end;
  }
  text += raw.slice(cursor);
  absorbed.text = text;
  return absorbed;
}

/** Re-exported so callers of the absorber can validate a day the same way. */
export { isChatSearchDay };
